import { Injectable } from '@angular/core';
import {
  OrgRole,
  PermissionKey,
  PermissionScope
} from '../permissions/permission.types';
import { AccessCacheService } from './access-cache.service';
import { OrgContextService } from './org-context.service';
import { PermissionMatrixStore } from './permission-matrix.store';
import { environment } from 'src/environments/environment';
import { UtilsService } from '..';

/**
 * Centralized permission evaluation that uses the cached role maps and current org role.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  constructor(
    private orgContext: OrgContextService,
    private accessCache: AccessCacheService,
    private matrixStore: PermissionMatrixStore,
    private utils: UtilsService
  ) {}

  /**
   * Returns true if the current user's role meets the requirement for `permission`.
   * `resourceId` is required for scoped permissions (project/card) to check cached memberships.
   */
  public can(permission: PermissionKey, resourceId?: string): boolean {
    this.utils.logger.info('Evaluating permission:', permission, 'with resourceId:', resourceId);

    const orgRole = this.orgContext.getCurrentOrgRole();
    if (orgRole === 'BillingOwner') {
      this.debug(permission, true, 'BillingOwner override', {
        orgRole,
        resourceId
      });
      return true;
    }

    const definition = this.matrixStore.getPermissionDefinition(permission);
    if (!definition) {
      this.debug(permission, false, 'Permission not found in runtime matrix', { resourceId });
      return false;
    }

    this.utils.logger.info("Definition: ", definition);

    const effectiveAllowedRoles = definition.effectiveAllowedRoles ?? [];

    this.utils.logger.info('Effective allowed roles for permission:', permission, effectiveAllowedRoles, 'Org role:', orgRole);

    if (definition.scope === 'org') {
      const allowed = this.isRoleExplicitlyAllowed(orgRole, effectiveAllowedRoles);
      this.debug(permission, allowed, allowed ? 'Allowed' : 'Org role not explicitly allowed', {
        scope: definition.scope,
        allowedRoles: effectiveAllowedRoles,
        orgRole
      });
      return allowed;
    }

    if (!resourceId) {
      if (this.isCreateLikePermission(permission)) {
        const allowed = this.isRoleExplicitlyAllowed(orgRole, effectiveAllowedRoles);
        this.debug(permission, allowed, 'Create permission evaluated at organization scope', {
          scope: definition.scope,
          allowedRoles: effectiveAllowedRoles,
          orgRole
        });
        return allowed;
      }

      this.debug(permission, false, 'Missing resourceId for scoped permission', {
        scope: definition.scope
      });
      return false;
    }

    if (this.shouldBypassResourceAccess(definition.scope, orgRole)) {
      this.debug(permission, true, 'Org role override (Owner/BillingOwner)', {
        scope: definition.scope,
        orgRole,
        resourceId
      });
      return true;
    }

    if (definition.scope === 'project') {
      const projectRole = this.accessCache.getProjectRole(resourceId);
      if (!projectRole) {
        this.debug(permission, false, 'Missing project membership role', {
          scope: definition.scope,
          orgRole,
          resourceId
        });
        return false;
      }
      const allowed = this.isRoleExplicitlyAllowed(projectRole, effectiveAllowedRoles);
      this.debug(permission, allowed, allowed ? 'Allowed' : 'Project role not explicitly allowed', {
        scope: definition.scope,
        allowedRoles: effectiveAllowedRoles,
        orgRole,
        projectRole,
        resourceId
      });
      return allowed;
    }

    if (definition.scope === 'card') {
      const cardRole = this.accessCache.getCardRole(resourceId);
      if (!cardRole) {
        this.debug(permission, false, 'Missing card permission role', {
          scope: definition.scope,
          orgRole,
          resourceId
        });
        return false;
      }
      const allowed = this.isRoleExplicitlyAllowed(cardRole, effectiveAllowedRoles);
      this.debug(permission, allowed, allowed ? 'Allowed' : 'Card role not explicitly allowed', {
        scope: definition.scope,
        allowedRoles: effectiveAllowedRoles,
        orgRole,
        cardRole,
        resourceId
      });
      return allowed;
    }

    this.debug(permission, false, 'Unsupported permission scope', {
      scope: (definition as { scope?: string }).scope,
      resourceId
    });
    return false;
  }

  private isRoleExplicitlyAllowed(role: string | null | undefined, allowedRoles: string[]): boolean {
    if (!role) return false;
    return allowedRoles.includes(role);
  }

  private shouldBypassResourceAccess(scope: PermissionScope, orgRole: OrgRole): boolean {
    if (orgRole !== 'Owner' && orgRole !== 'BillingOwner') return false;
    if (scope === 'project') return this.matrixStore.getOrgAdminsImplicitProjectAccess();
    if (scope === 'card') return this.matrixStore.getOrgAdminsImplicitCardAccess();
    return false;
  }

  public canAsync(permission: PermissionKey, resourceId?: string): Promise<boolean> {
    return this.canWithReadyContext(permission, resourceId);
  }

  private async canWithReadyContext(permission: PermissionKey, resourceId?: string): Promise<boolean> {
    await Promise.all([
      this.orgContext.ensureOrgContextReady(),
      this.matrixStore.preloadCurrentOrg()
    ]);
    return this.can(permission, resourceId);
  }

  private isCreateLikePermission(permission: PermissionKey): boolean {
    return typeof permission === 'string' && permission.endsWith('.Create');
  }

  /** Emit a debug trace when the app is running in debug mode. */
  private debug(permission: PermissionKey, allowed: boolean, reason: string, meta?: Record<string, unknown>): void {
    if (!environment.isDebugMode || environment.production) return;
    const payload = {
      permission,
      allowed,
      reason,
      ...(meta || {})
    };
    // eslint-disable-next-line no-console
    console.debug('[PermissionsService.can]', payload);
  }
}
